import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Loader2, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur');

      toast.success('Message envoyé avec succès !');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact - CEFAT Business School Abidjan"
        description="Contactez CEFAT Business School Abidjan : Cocody Angre 8e Tranche, +225 07 12 65 17 19. Horaires : Lun-Ven 8h-17h, Sam 8h-12h. Demande d'information, preinscription."
        keywords="contact CEFAT Abidjan, adresse CEFAT Cocody, telephone CEFAT, ecole de commerce Abidjan contact, formation Abidjan renseignements"
        url="/contact"
      />
      {/* Hero */}
      <section className="bg-[#003366] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1 
            {...fadeInUp}
            className="font-heading text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Contactez-nous
          </motion.h1>
          <motion.p 
            {...fadeInUp}
            className="text-white/80 text-lg max-w-2xl mx-auto"
          >
            Notre équipe est à votre disposition pour répondre à toutes vos questions.
          </motion.p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div {...fadeInUp}>
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="font-heading text-2xl font-bold text-[#003366] mb-6">
                  Envoyez-nous un message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#003366] font-semibold">
                        Nom complet <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-2 h-12"
                        placeholder="Votre nom"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#003366] font-semibold">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-2 h-12"
                        placeholder="votre@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-[#003366] font-semibold">
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2 h-12"
                        placeholder="+225 07 00 00 00 00"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-[#003366] font-semibold">
                        Sujet <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="mt-2 h-12"
                        placeholder="Sujet de votre message"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#003366] font-semibold">
                      Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-2 min-h-[150px]"
                      placeholder="Votre message..."
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full py-3 font-semibold"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={18} />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={18} />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div {...fadeInUp}>
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="font-heading text-2xl font-bold text-[#003366] mb-6">
                    Informations de contact
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="text-[#003366]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#003366]">Téléphone</h3>
                        <p className="text-gray-600">+225 07 12 65 17 19</p>
                        <p className="text-gray-600">+225 01 00 95 95 18</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="text-[#003366]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#003366]">Email</h3>
                        <p className="text-gray-600">cefat.bs@gmail.com</p>
                        <a href="mailto:contact@cefat-bs.ci" className="text-gray-600 hover:underline block">contact@cefat-bs.ci</a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-[#003366]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#003366]">Adresse</h3>
                        <p className="text-gray-600">Cocody Angré 8e Tranche</p>
                        <p className="text-gray-600">Carrefour Prière, Rue L259</p>
                        <p className="text-gray-600">Abidjan, Côte d'Ivoire</p>
                        <p className="text-gray-500 text-sm mt-1">BP 1238, Rue William Ipot2 (L84)</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="text-[#003366]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#003366]">Horaires d'ouverture</h3>
                        <p className="text-gray-600">Lundi - Vendredi : 8h00 - 17h00</p>
                        <p className="text-gray-600">Samedi : 8h00 - 12h00</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Globe className="text-[#003366]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#003366]">Groupe CEFAT</h3>
                        <a href="https://universitecefat.ci" target="_blank" rel="noopener noreferrer" className="text-[#DC143C] hover:underline">
                          universitecefat.ci
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="h-64 bg-gray-200">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3972.0!2d-3.9759!3d5.3928!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNcKwMjMnMzQuMSJOIDPCsDU4JzMzLjIiVw!5e0!3m2!1sfr!2sci!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="CEFAT Business School - Cocody Angré"
                    />
                  </div>
                  <div className="p-4 bg-[#003366] text-white text-center">
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=5.3928,-3.9759" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#FFD700] hover:underline font-medium"
                    >
                      Ouvrir dans Google Maps →
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Campus Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="badge badge-blue mb-4">Notre Campus</span>
            <h2 className="font-heading text-3xl font-bold text-[#003366]">
              CEFAT Business School Abidjan
            </h2>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#F8FAFC] rounded-xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-[#003366] rounded-lg flex items-center justify-center mx-auto mb-6">
                <MapPin className="text-white" size={32} />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#003366] mb-2">Campus Principal</h3>
              <p className="text-gray-600 mb-2">Cocody Angré 8e Tranche, Carrefour Prière</p>
              <p className="text-gray-600 mb-2">Rue L259, 3609</p>
              <p className="text-gray-600 mb-4">Abidjan, Côte d'Ivoire</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+2250712651719" className="text-[#DC143C] font-medium hover:underline">
                  +225 07 12 65 17 19
                </a>
                <a href="tel:+2250100959518" className="text-[#DC143C] font-medium hover:underline">
                  +225 01 00 95 95 18
                </a>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                Membre du groupe <a href="https://universitecefat.ci" target="_blank" rel="noopener noreferrer" className="text-[#003366] font-medium hover:underline">Université CEFAT</a>
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
